import React, {useEffect, useState} from 'react';
import {useField, useFormikContext} from 'formik';

import {Column, Row} from '../layout';
import AppTextInput from '../input/app-text-input';
import ErrorMessage from './ErrorMessage';
import './styles.scss';
import passwordEyeHide from '../../assets/password-eye.svg';
import passwordEyeVisibleCrossed from '../../assets/password-eye-visible-crossed.svg';
import passwordEyeVisibleNotCrossed from '../../assets/password-eye-visible-not-crossed.svg';
import {Icons} from '../../util/icons';
import colors from '../../util/colors';
import Icon from '../icon';
import {Store} from '../../store';

interface AppFormFieldProps extends React.HTMLProps<HTMLInputElement> {
  name: string;
  showErrorMessage?: boolean;
  handleFormikContext?(formik: any): void;
  className?: string;
  modifyDisplayValue?(value: string): string;
  error?: string;
  onChange?(value: any): void;
  autoComplete?: string;
  clearStyle?: boolean;
  errorContainerStyle?: any;
  containerStyle?: string;
  passwordIcon?: boolean;
  handlePasswordIconClick?(): void;
  showPassword?: boolean;
  pwdIconClass?: string;
}

function AppFormField({
  name,
  showErrorMessage = false,
  handleFormikContext,
  className,
  modifyDisplayValue,
  error,
  onChange,
  autoComplete,
  clearStyle,
  errorContainerStyle,
  passwordIcon,
  handlePasswordIconClick,
  showPassword,
  pwdIconClass,
  ...rest
}: AppFormFieldProps) {
  const formik = useFormikContext();
  const [field] = useField(name);
  const [value, setValue] = useState(
    modifyDisplayValue ? modifyDisplayValue(field.value) : field.value
  );
  const [modify, setModify] = useState(true);
  const {setFieldTouched, handleChange, errors, touched, submitCount} = formik;
  const newError =
    typeof error === 'boolean'
      ? Store.getState().strings.value.pt.messages.requiredField
      : error || (errors as any)[name];

  const formikError = (errors as any)[name];
  const visible = (touched as any)[name] || submitCount > 0;

  // Final error (only show if visible)
  const displayError = error ?? (visible ? formikError : undefined);

  useEffect(() => {
    if (!!modifyDisplayValue) {
      if (modifyDisplayValue(field.value) !== value) {
        setValue(field.value);
      }
    } else if (field.value !== value) {
      setValue(field.value);
    }
  }, [field.value]);
  if (!!handleFormikContext) {
    handleFormikContext(formik);
  }

  return (
    <>
      {!passwordIcon ? (
        <Column className={!clearStyle ? 'app-form-field-column-content' : ''}>
          {showErrorMessage && !!displayError && formik.submitCount > 0 && (
            <div className={'app-form-field-error-content'}>
              <Icon
                //style={{backgroundColor: 'white'}}
                changeViewbox
                viewBox={'0 0 24 24'}
                width="24"
                height="24"
                name={Icons.ALERT_PROBLEM}
                color={colors.alertRed}
              />

              <ErrorMessage error={newError} visible={!!displayError} />
            </div>
          )}
          <AppTextInput
            autoComplete={autoComplete}
            className={`${className}${
              showErrorMessage && !!displayError && formik.submitCount > 0
                ? ' invalid-app-form-field'
                : ' '
            }`}
            value={modify ? value : field.value}
            onClick={() => setModify(false)}
            onChange={
              onChange
                ? (value) => {
                    onChange(value);
                    handleChange(name);
                  }
                : handleChange(name)
            }
            onBlur={() => {
              setFieldTouched(name);
              if (modifyDisplayValue) {
                setValue(modifyDisplayValue(value));
              }
              setModify(true);
            }}
            error={displayError}
            onFocus={() => {
              // Clear the error when user focuses the field
              formik.setFieldError(name, undefined);
            }}
            {...rest}
          />
        </Column>
      ) : (
        <Column className={!clearStyle ? 'app-form-field-column-content' : ''}>
          {showErrorMessage && !!displayError && (
            <div
              className={'app-form-field-error-content'}
              style={errorContainerStyle}>
              <Icon
                // style={{backgroundColor: 'white'}}
                changeViewbox
                viewBox={'0 0 24 24'}
                width="24"
                height="24"
                name={Icons.ALERT_PROBLEM}
                color={colors.alertRed}
              />
              <ErrorMessage error={displayError} visible={!!displayError} />
            </div>
          )}
          <AppTextInput
            autoComplete={autoComplete}
            className={`${className}${
              showErrorMessage && !!displayError
                ? ' invalid-app-form-field'
                : ' '
            }`}
            value={modify ? value : field.value}
            onClick={() => setModify(false)}
            onChange={
              onChange
                ? (value) => {
                    onChange(value);
                    handleChange(name);
                  }
                : handleChange(name)
            }
            onBlur={() => {
              setFieldTouched(name);
              if (modifyDisplayValue) {
                setValue(modifyDisplayValue(value));
              }
              setModify(true);
            }}
            onFocus={() => {
              // Clear the error when user focuses the field
              formik.setFieldError(name, undefined);
            }}
            error={displayError}
            endIcon={
              passwordIcon && field.value ? (
                <img
                  className={pwdIconClass ? pwdIconClass : 'password-eye'}
                  onClick={handlePasswordIconClick}
                  src={
                    !showPassword
                      ? passwordEyeVisibleCrossed
                      : passwordEyeVisibleNotCrossed
                  }
                />
              ) : (
                passwordIcon && (
                  <img
                    className={pwdIconClass ? pwdIconClass : 'password-eye'}
                    src={passwordEyeHide}
                  />
                )
              )
            }
            {...rest}
          />
        </Column>
      )}
    </>
  );
}

export default AppFormField;
